import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import { URL_PREFIXES } from '@constants/routes';

// Helper functions to add/remove prefixes.
export const removeOrgPrefix = (orgId: string) => orgId.replace(URL_PREFIXES.ORGANIZATION, '');
export const removeProgPrefix = (progId: string) => progId.replace(URL_PREFIXES.PROGRAM, '');

// add* are idempotent: strip any existing prefix first so passing an already-prefixed
// id (e.g. a raw `:orgId`/`:progId` route param from useParams) doesn't double-prefix
// the URL (`org-org-…`). Org/program ids are Mongo ObjectID hex, so they can never
// legitimately start with `org-`/`prog-` — stripping is collision-proof.
export const addOrgPrefix = (orgId: string) =>
  `${URL_PREFIXES.ORGANIZATION}${removeOrgPrefix(orgId)}`;
export const addProgPrefix = (progId: string) =>
  `${URL_PREFIXES.PROGRAM}${removeProgPrefix(progId)}`;

// Build route paths with org and prog context
export const buildRoutePath = (path: string, orgId: string, progId: string) => {
  return `/${addOrgPrefix(orgId)}/${addProgPrefix(progId)}${path}`;
};

// Build route paths with org context only
export const buildOrgRoutePath = (path: string, orgId: string) => {
  return `/${addOrgPrefix(orgId)}${path}`;
};

// Build a complete case URL from case details
export const buildCaseUrl = (
  caseDetails: Pick<GetCaseRecordResponse, 'id' | 'organization_id' | 'program_id'>,
): string => {
  const baseUrl = window.location.origin;
  const { id, organization_id, program_id } = caseDetails;

  if (!id) {
    return baseUrl;
  }

  if (organization_id && program_id) {
    // Include org and program in the URL for full context with proper prefixes
    return `${baseUrl}/${addOrgPrefix(organization_id)}/${addProgPrefix(program_id)}/cases/${id}`;
  }

  // Fallback to direct case URL (will use redirect route)
  return `${baseUrl}/cases/${id}`;
};
